/**
 * Ponte entre o modelo atual do backend (moedas uau/mandou/valeu em Gigabytes,
 * comportamentos) e a terminologia da nova campanha (selos, níveis
 * #RECONHECIMENTO/#DESTAQUE/#REFERÊNCIA, dimensões da Cultura).
 *
 * Enquanto o backend não migrar, a conversão vale: 1 selo = 100 Gigabytes
 * (valeu=100, mandou=200, uau=300). Quando a migração acontecer, este módulo
 * é o único ponto a ajustar.
 *
 * Os `id`s internos dos níveis (semeou/cresceu/floriu) são mantidos por serem
 * chaves de som/animação; apenas a terminologia visível (tag/selos) mudou.
 */

export const GIGABYTES_POR_SELO = 100;

export type NivelId = 'semeou' | 'cresceu' | 'floriu';

export interface Nivel {
  id: NivelId;
  tag: string;
  celebracao: string;
  selos: number;
  descricao: string;
}

const NIVEL_POR_MOEDA: Record<string, Nivel> = {
  valeu: {
    id: 'semeou',
    tag: '#RECONHECIMENTO',
    celebracao: 'Reconheceu!',
    selos: 1,
    descricao:
      'Início do movimento: comportamento relevante e de qualidade, alinhado aos valores da instituição em uma ação específica.',
  },
  mandou: {
    id: 'cresceu',
    tag: '#DESTAQUE',
    celebracao: 'Destacou!',
    selos: 2,
    descricao:
      'Desenvolvimento e consistência: excelência que supera expectativas, com impacto positivo perceptível para a equipe, área ou processo.',
  },
  uau: {
    id: 'floriu',
    tag: '#REFERÊNCIA',
    celebracao: 'Referenciou!',
    selos: 3,
    descricao:
      'Maturidade e inspiração: impacto extraordinário, exemplar ou inspirador, tornando-se referência na instituição.',
  },
};

export const NIVEIS: Nivel[] = [NIVEL_POR_MOEDA.valeu, NIVEL_POR_MOEDA.mandou, NIVEL_POR_MOEDA.uau];

export function nivelByMoeda(moeda?: string): Nivel {
  return NIVEL_POR_MOEDA[moeda || ''] || NIVEL_POR_MOEDA.valeu;
}

export function gigabytesToSelos(gigabytes?: number): number {
  if (!gigabytes || isNaN(gigabytes) || gigabytes < 0) return 0;
  return Math.floor(gigabytes / GIGABYTES_POR_SELO);
}

export interface Dimensao {
  id: string;
  label: string;
  color: string;
  descricao: string;
}

/**
 * Metadados de TODAS as dimensões conhecidas do backend (por `behavior`).
 * Mantido completo para exibir corretamente reconhecimentos históricos, mesmo
 * de comportamentos que não são mais selecionáveis (ex.: resolutividade).
 */
const DIMENSAO_META: Record<string, { label: string; color: string; descricao: string }> = {
  cooperacao: {
    label: 'Cooperação',
    color: '#1aabe3',
    descricao:
      'Colabora com colegas e áreas, compartilha conhecimento e contribui para soluções conjuntas, promovendo sinergia e trabalho em equipe.',
  },
  iniciativa: {
    label: 'Iniciativa',
    color: '#f59120',
    descricao: 'Demonstra protagonismo ao antecipar demandas, propor melhorias e assumir responsabilidades além do esperado.',
  },
  integridade: {
    label: 'Integridade',
    color: '#2fa457',
    descricao: 'Age com ética e responsabilidade, cumprindo normas e protegendo a governança e a reputação institucional.',
  },
  lideranca: {
    label: 'Liderança Inspiradora',
    color: '#e0629b',
    descricao: 'Mobiliza equipes pelo exemplo, promove ambiente de confiança e desenvolve pessoas.',
  },
};

/** Comportamentos selecionáveis na campanha atual (as 4 dimensões da Cultura). */
const DIMENSOES_SELECIONAVEIS = ['cooperacao', 'iniciativa', 'integridade', 'lideranca'];

export const DIMENSOES: Dimensao[] = DIMENSOES_SELECIONAVEIS.map(id => ({ id, ...DIMENSAO_META[id] }));

export function dimensaoByBehavior(behavior?: string): Dimensao {
  const meta = DIMENSAO_META[behavior || ''];
  return meta ? { id: behavior as string, ...meta } : DIMENSOES[0];
}

/**
 * Primeiro nome de exibição do jogador, resiliente à mudança do player_fields.
 * O esqueleto novo usa `nome` (sem `nome_guerra`); mantemos o fallback para
 * `nome_guerra` (esqueleto antigo) e, por fim, para o `name` do player Funifier.
 */
export function displayFirstName(userInfo: any, user?: any): string {
  const full = userInfo?.nome_guerra || userInfo?.nome || user?.name || '';
  return String(full).trim().split(' ')[0] || '';
}

/** Converte um hex (#rrggbb) em rgba com alpha, para brilhos e fundos suaves. */
export function hexToRgba(hex: string, alpha: number): string {
  let h = (hex || '#1aabe3').replace('#', '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
